import { z } from 'zod';
import { Cents, IsoDate, IsoDateTime, Uuid } from './common.js';
import { ManualSubscription } from './manual-subscriptions.js';
import { ProductCategory } from './settings.js';

/**
 * Contrat des routes `GET /api/metrics/*` (CDC §3.7, lot B). Ces schémas sont la référence du front
 * (lot B2 : écrans Chiffres, Dépenses, Trésorerie). Tous les montants sont en centimes HT (sauf mention),
 * les mois en `AAAA-MM`, les dates en `AAAA-MM-JJ`.
 *
 * Source des chiffres d'un mois (règle de préséance) : les données Axonaut quand Axonaut couvre ce mois,
 * sinon l'historique importé du Google Sheet (`history_monthly`, lignes `expenses` de source `import`).
 * Axonaut couvre tous les mois à partir de sa première donnée : première facture pour le CA (facturé,
 * signé, encaissé, produits, devis), première dépense ou ligne bancaire pour les dépenses. Les mois
 * antérieurs viennent de l'import. Les chiffres qu'Axonaut ne fournit pas (R1, canaux d'acquisition,
 * prévisions de dépenses) viennent toujours de l'historique (et des attributions saisies dans l'appli).
 */

export const MonthKey = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Mois attendu au format AAAA-MM');
export type MonthKey = z.infer<typeof MonthKey>;

/** D'où viennent les chiffres d'un mois : Axonaut, import du Sheet, ou rien (mois futur ou sans donnée). */
export const MonthSource = z.enum(['axonaut', 'import', 'none']);
export type MonthSource = z.infer<typeof MonthSource>;

/** `?year=` (par défaut : l'année de la date du jour du serveur). */
export const MetricsYearQuery = z.object({ year: z.coerce.number().int().min(2000).max(2100).optional() });
export type MetricsYearQuery = z.infer<typeof MetricsYearQuery>;

// ------------------------------------------------------------------ GET /api/metrics/revenue?year=

export const RevenueMonth = z.object({
  month: MonthKey,
  /** CA facturé HT (somme des factures datées du mois). null pour un mois futur. */
  invoicedCents: Cents.nullable(),
  /**
   * CA signé HT (devis acceptés dans le mois, date de réponse du client), en part d'Evolusite (lot AY : moins la
   * sous-traitance ; mois du Sheet : « CA signé moins la sous-traitance » quand il existe). null pour un mois futur.
   */
  signedCents: Cents.nullable(),
  /** CA encaissé (paiements datés du mois). null pour un mois futur. */
  cashedCents: Cents.nullable(),
  /** Objectif du mois (table `objectives`), null s'il n'est pas défini. */
  objectiveCents: Cents.nullable(),
  /** CA facturé du même mois de l'année précédente (même règle de préséance), null si inconnu. */
  previousYearInvoicedCents: Cents.nullable(),
  /** Source des valeurs facturé/signé/encaissé du mois. */
  source: MonthSource,
  /** Mois postérieur au mois en cours. */
  future: z.boolean(),
});
export type RevenueMonth = z.infer<typeof RevenueMonth>;

export const ProductMixItem = z.object({
  category: ProductCategory,
  label: z.string(),
  amountCents: Cents,
});
export type ProductMixItem = z.infer<typeof ProductMixItem>;

/** Lot V : devis à 0 € (`isZeroAmount`) exclus des nombres et du taux de conversion. */
export const QuoteStats = z.object({
  /** Devis émis (date du devis) de janvier au mois en cours. */
  issued: z.int().min(0),
  /** Devis acceptés (date de réponse) sur la même période. */
  signed: z.int().min(0),
  /** Devis refusés (date de réponse) sur la même période. */
  lost: z.int().min(0),
  /** signed / issued, null si aucun devis émis. */
  conversionRate: z.number().nullable(),
  /** Nombre de R1 de l'année (historique importé ; agenda au lot D), null si inconnu. */
  r1: z.int().min(0).nullable(),
});
export type QuoteStats = z.infer<typeof QuoteStats>;

export const RevenueMetrics = z.object({
  year: z.int(),
  /** Date du jour du serveur (figée par FAKE_TODAY en démo). */
  today: IsoDate,
  /** 12 mois, de janvier à décembre. */
  months: z.array(RevenueMonth).length(12),
  totals: z.object({
    invoicedCents: Cents,
    signedCents: Cents,
    cashedCents: Cents,
    objectiveCents: Cents,
    /** Facturé de l'année précédente sur les mêmes mois écoulés (janvier au mois en cours). */
    previousYearInvoicedCents: Cents,
  }),
  /** Signé par produit (devis acceptés de janvier au mois en cours, hors abonnements), du plus gros au plus petit. */
  productsMix: z.array(ProductMixItem),
  /**
   * Lot AY : sous-traité de la période (signé brut − part d'Evolusite), hors du total : `signedCents` et `productsMix`
   * comptent la part d'Evolusite (une ligne sous-traitée sort de son produit à hauteur de son coût).
   */
  subcontractedCents: Cents.default(0),
  quotes: QuoteStats,
});
export type RevenueMetrics = z.infer<typeof RevenueMetrics>;

// ------------------------------------------------------------------ GET /api/metrics/overview

/** Tuiles de l'onglet Chiffres de la maquette. */
export const OverviewMetrics = z.object({
  today: IsoDate,
  year: z.int(),
  /** « CA facturé 2026 » : facturé de l'année à date, comparé à l'année précédente sur les mois écoulés. */
  invoiced: z.object({
    amountCents: Cents,
    /** Dernier mois complet pris pour la comparaison (ex. « à fin sept. »), null en janvier. */
    comparedThrough: MonthKey.nullable(),
    /** Facturé de l'année jusqu'à `comparedThrough` inclus. */
    currentPeriodCents: Cents,
    /** Facturé de l'année précédente sur les mêmes mois. */
    previousPeriodCents: Cents,
    /** currentPeriod / previousPeriod - 1 (0,47 = +47 %), null si pas de référence. */
    growthRate: z.number().nullable(),
  }),
  /**
   * Lot AG [12] : « CA signé en octobre » : CA signé du mois en cours (devis acceptés hors devis à 0 € + abonnements
   * démarrés dans le mois × 12, HT ; mois venant du Sheet : signé de l'historique) face à l'objectif de CA signé du
   * mois. Égal à `RevenueDetailsMetrics.months[mois en cours].signed.amountCents`.
   */
  signedThisMonth: z.object({
    month: MonthKey,
    amountCents: Cents,
    /** Part des devis signés. */
    quotesCents: Cents,
    /** Part des abonnements démarrés dans le mois (× 12). */
    subscriptionsCents: Cents,
    /**
     * Lot AY : `amountCents` est la PART D'EVOLUSITE (devis moins leur sous-traitance) ; `grossCents` = signé brut,
     * `subcontractedCents` = sous-traité (« 9 600 € signés, dont 1 900 € sous-traités »). Mois du Sheet : « CA signé
     * moins la sous-traitance » quand il existe.
     */
    grossCents: Cents.default(0),
    subcontractedCents: Cents.default(0),
    objectiveCents: Cents.nullable(),
    /** amount / objective, null sans objectif. */
    ratio: z.number().nullable(),
  }),
  /** « Devis en attente » (lot Q : hors devis à 0 €, cf. `isZeroAmount`). */
  pendingQuotes: z.object({
    count: z.int().min(0),
    amountCents: Cents,
    /** Devis envoyés (ou datés) depuis plus de 30 jours. */
    olderThan30Days: z.int().min(0),
  }),
  /** « Reste à encaisser » : factures de l'année non soldées (`outstanding_cents`). */
  outstanding: z.object({ amountCents: Cents, count: z.int().min(0) }),
  /** « Résultat cumulé » : facturé − dépenses réelles, de janvier au dernier mois complet. */
  cumulativeResult: z.object({ amountCents: Cents, through: MonthKey.nullable() }),
  /**
   * « Abonnements » : revenu mensuel récurrent et abonnés payants (cf. /subscriptions), abonnements hors
   * Stripe compris (lot J) ; `manualMrrCents` = la part hors Stripe (« dont X € hors Stripe »).
   */
  subscriptions: z.object({ mrrCents: Cents, payingCount: z.int().min(0), manualMrrCents: Cents }),
});
export type OverviewMetrics = z.infer<typeof OverviewMetrics>;

// ------------------------------------------------------------------ GET /api/metrics/expenses?year=

export const ExpenseCell = z.object({
  month: MonthKey,
  /** Valeur affichée : le réel pour un mois écoulé, la prévision pour le mois en cours et les suivants. */
  amountCents: Cents,
  /** Réel constaté (pour le mois en cours : dépensé à date ; 0 pour un mois futur). */
  actualCents: Cents,
  /** Prévision (mois en cours et suivants), null pour un mois écoulé. */
  forecastCents: Cents.nullable(),
  forecast: z.boolean(),
  /**
   * Lot P : origine de la prévision d'un poste (mois en prévision) : saisie à la main (`manual`), estimation
   * calculée par Claude (`ai`) ou sans lui (`average` = moyenne des 3 derniers mois complets, `seasonal` = même
   * mois de l'an dernier), prévision du Sheet (`import`). Absent ou null hors prévision.
   */
  origin: z.enum(['manual', 'ai', 'average', 'seasonal', 'import']).nullable().optional(),
});
export type ExpenseCell = z.infer<typeof ExpenseCell>;

export const ExpenseCategoryRow = z.object({
  id: Uuid,
  key: z.string(),
  name: z.string(),
  kind: z.enum(['fixed', 'variable']),
  position: z.int(),
  /** Lot P : les lignes bancaires (TTC) de ce poste sont comptées HT (÷ 1,2). */
  vatRecoverable: z.boolean(),
  /**
   * Lot AH [21] : poste archivé. Il n'est rendu que s'il a du réel dans l'année ; ses mois à venir valent 0 et ne
   * se saisissent pas, et il n'est pas proposé dans les listes de catégorisation.
   */
  archived: z.boolean(),
  /** 12 cellules, de janvier à décembre. */
  cells: z.array(ExpenseCell).length(12),
  /** Somme des valeurs affichées de l'année (réel + prévisions). */
  totalCents: Cents,
});
export type ExpenseCategoryRow = z.infer<typeof ExpenseCategoryRow>;

/**
 * Origine des prévisions d'un mois : prévision importée du Sheet, moyenne des 3 derniers mois complets, ou
 * (lot P) estimation enregistrée (calculée ou saisie) pour au moins un poste.
 */
export const ForecastSource = z.enum(['import', 'average', 'estimate', 'none']);
export type ForecastSource = z.infer<typeof ForecastSource>;

export const YearEndProjection = z.object({
  actualCents: Cents,
  estimatedCents: Cents,
  totalCents: Cents,
});
export type YearEndProjection = z.infer<typeof YearEndProjection>;

export const ExpensesMetrics = z.object({
  year: z.int(),
  today: IsoDate,
  months: z.array(MonthKey).length(12),
  /** Premier mois en prévision (le mois en cours), null si toute l'année est écoulée. */
  forecastFrom: MonthKey.nullable(),
  /** Source du réel de chaque mois (préséance Axonaut puis import). */
  sources: z.array(z.object({ month: MonthKey, source: MonthSource, forecastSource: ForecastSource })),
  /** Postes, dans l'ordre (`position`), fixes puis variables. */
  categories: z.array(ExpenseCategoryRow),
  /** Dépenses sans poste (lignes à catégoriser), par mois : réel seulement. */
  uncategorized: z.object({
    /** Nombre total de lignes à catégoriser (toutes années). */
    count: z.int().min(0),
    cells: z.array(ExpenseCell).length(12),
    totalCents: Cents,
  }),
  /**
   * Total de chaque mois : postes + à catégoriser pour un mois écoulé ; pour un mois en prévision, somme des
   * prévisions des postes (les lignes à catégoriser du mois en cours n'y sont pas ajoutées).
   */
  totals: z.array(ExpenseCell).length(12),
  /** Lot P : lignes de l'année qui ne sont pas des dépenses (virements internes, TVA…) ou des doublons. */
  excluded: z.object({ count: z.int().min(0), totalCents: Cents }),
  /** Lot P : prédictions de l'IA peu sûres à vérifier (toutes années). */
  review: z.object({ count: z.int().min(0) }),
  /**
   * Lot P : « Fin d'année estimée » (année en cours seulement, sinon null) : réel à date (mois écoulés et mois
   * en cours, lignes sans poste comprises) + estimation de ce qui reste par poste.
   */
  yearEnd: YearEndProjection.nullable(),
  summary: z.object({
    /** Dernier mois complet pris en compte (ex. septembre), null en janvier. */
    through: MonthKey.nullable(),
    /** Dépenses réelles de janvier à `through`. */
    actualTotalCents: Cents,
    monthsCount: z.int().min(0),
    monthlyAverageCents: Cents,
    /** Part des charges fixes dans la moyenne mensuelle. */
    fixedMonthlyAverageCents: Cents,
    /** CA facturé sur les mêmes mois. */
    invoicedCents: Cents,
    /** actualTotal / invoiced (0,95 = 95 %), null sans CA. */
    ratioToInvoiced: z.number().nullable(),
    /** Mois le plus lourd parmi les mois écoulés, avec ses deux plus gros postes variables (ce qui l'explique). */
    heaviestMonth: z
      .object({
        month: MonthKey,
        amountCents: Cents,
        topCategories: z.array(z.object({ name: z.string(), amountCents: Cents })),
      })
      .nullable(),
  }),
});
export type ExpensesMetrics = z.infer<typeof ExpensesMetrics>;

// ------------------------------------------------------------------ GET /api/metrics/cashflow?weeks=13

export const CashflowQuery = z.object({ weeks: z.coerce.number().int().min(1).max(52).default(13) });
export type CashflowQuery = z.infer<typeof CashflowQuery>;

/**
 * Lot AE : d'où vient la date d'un solde de devis signé. `planning` : R5 prévu du projet + 15 jours ; `placed` :
 * projet pas encore calé (ou pas encore de projet), placé virtuellement avec le simulateur du planning, dans l'ordre
 * de signature (R5 simulé + 15 jours) ; `estimate` : devis qui n'attend pas de projet de site (« Pas de projet pour
 * ce devis », aucune ligne vitrine, e-commerce ou refonte) : aujourd'hui + 60 jours.
 */
export const BalanceDateOrigin = z.enum(['planning', 'placed', 'estimate']);
export type BalanceDateOrigin = z.infer<typeof BalanceDateOrigin>;

/** Libellé court de l'origine d'une date de solde (« planning », « placé (non calé) », « estimé »). */
export const BALANCE_DATE_ORIGIN_LABELS: Record<BalanceDateOrigin, string> = {
  planning: 'planning',
  placed: 'placé (non calé)',
  estimate: 'estimé (sans projet)',
};

export const CashflowItem = z.object({
  /**
   * `invoice` : facture non soldée, à sa date d'échéance. Lot Q : une facture échue (échéance avant aujourd'hui)
   * est attendue la semaine prochaine (semaine 1), jamais comptée « tout de suite » en semaine 0 ; échue depuis
   * plus de `settings.rules.cashflowOverdue.excludeAfterDays` jours (60 par défaut), elle n'est pas comptée du
   * tout (`uncountedOverdue`). Un montant à facturer de devis signé dont la date attendue est passée est aussi
   * attendu en semaine 1 ;
   * `subscriptions` : revenu mensuel récurrent ramené à la semaine (× 12 / 52) ;
   * `expense_forecast` : prévision de dépenses d'un poste, étalée jour par jour sur le mois (pour le mois
   * en cours : reste à dépenser = prévision − réel, étalé sur les jours restants) ;
   * `quote_deposit` : acompte de 50 % encore à facturer d'un devis accepté sans facture, attendu à J+15 ;
   * `quote_balance` : reste à facturer d'un devis accepté, attendu au R5 du projet + 15 jours ; lot AE : projet pas
   * encore calé (ou pas encore de projet) placé virtuellement avec le simulateur du planning (`dateOrigin`). Seuls les devis acceptés depuis moins de 180 jours sont pris en compte
   * (lot V : sauf ceux dont le projet rattaché n'est pas terminé, quel que soit leur âge) ; lot Q : les factures du
   * client non rattachées à un devis, émises depuis la signature, comptent comme déjà facturées (comme la règle
   * `invoice_create.deposit`), pour ne pas compter deux fois le même acompte. Aucune ligne à 0 € ;
   * `subcontracting` (lot AY) : ce qui reste à payer à un sous-traitant pour un devis signé (coût confié − déjà payé
   * de ses factures rattachées), à la date « au plus tard » ; date passée : semaine 1, `overdue` (« en retard »).
   * Quand la sous-traitance est suivie, le poste « Sous-traitance » n'est plus estimé (pas de double comptage).
   */
  kind: z.enum([
    'invoice',
    'subscriptions',
    'expense_forecast',
    'quote_deposit',
    'quote_balance',
    'subcontracting',
  ]),
  label: z.string(),
  /** Positif = entrée, négatif = sortie. */
  amountCents: Cents,
  /** Date de l'échéance (facture, même échue et placée en semaine 1) ou premier jour concerné de la semaine. */
  date: IsoDate,
  invoiceId: Uuid.nullable(),
  categoryId: Uuid.nullable(),
  /** Devis d'origine (`quote_deposit`, `quote_balance`). */
  quoteId: Uuid.nullable(),
  /** Facture échue (date d'échéance passée). */
  overdue: z.boolean(),
  /** Lot AE : d'où vient la date d'un solde (`quote_balance`), sinon absent. */
  dateOrigin: BalanceDateOrigin.optional(),
  /** Lot AY : précision d'une sortie de sous-traitance (« 1 900 € − 600 € déjà versés »), sinon absente. */
  detail: z.string().optional(),
});
export type CashflowItem = z.infer<typeof CashflowItem>;

export const CashflowBucket = z.object({
  index: z.int().min(0),
  /** Lundi de la semaine. */
  start: IsoDate,
  /** Dimanche de la semaine. */
  end: IsoDate,
  inflowsCents: Cents,
  /** Négatif (somme des sorties). */
  outflowsCents: Cents,
  netCents: Cents,
  /** Solde prévu en fin de semaine, null si le solde de départ est inconnu. */
  balanceCents: Cents.nullable(),
  items: z.array(CashflowItem),
});
export type CashflowBucket = z.infer<typeof CashflowBucket>;

export const PendingQuote = z.object({
  id: Uuid,
  number: z.string().nullable(),
  title: z.string().nullable(),
  clientId: Uuid.nullable(),
  clientName: z.string().nullable(),
  amountCents: Cents,
  date: IsoDate.nullable(),
  sentAt: IsoDateTime.nullable(),
  /** Jours depuis l'envoi (ou la date du devis). */
  ageDays: z.int().nullable(),
});
export type PendingQuote = z.infer<typeof PendingQuote>;

/**
 * Lot Q : familles de l'explication du solde (« solde dans 13 semaines = solde actuel + factures + abonnements +
 * devis signés − dépenses »). `invoices` ← `invoice` ; `subscriptions` ← `subscriptions` ; `signed_quotes` ←
 * `quote_deposit` et `quote_balance` ; `expenses` ← `expense_forecast`.
 */
export const CashflowExplanationKind = z.enum(['invoices', 'subscriptions', 'signed_quotes', 'expenses']);
export type CashflowExplanationKind = z.infer<typeof CashflowExplanationKind>;

export const CashflowExplanationLine = z.object({
  kind: CashflowExplanationKind,
  /** « Factures à encaisser », « Abonnements », « Devis signés à facturer », « Dépenses prévues ». */
  label: z.string(),
  /** D'où vient le chiffre (une phrase). */
  hint: z.string(),
  /** Somme signée des lignes de la famille, de la semaine 0 à `throughIndex` inclus (négatif pour les dépenses). */
  amountCents: Cents,
  /** Nombre d'éléments distincts : factures, devis, postes de dépenses ; abonnés payants pour `subscriptions`. */
  count: z.int().min(0),
});
export type CashflowExplanationLine = z.infer<typeof CashflowExplanationLine>;

/**
 * Explication d'un solde prévu : `balanceCents = startingBalanceCents + Σ lines.amountCents`, au centime près
 * (null quand le solde de départ est inconnu ; les lignes restent données). Les 4 familles sont toujours
 * présentes, dans l'ordre de `CashflowExplanationKind`, même à 0.
 */
export const CashflowExplanation = z.object({
  /** Dernière semaine incluse ; null = aujourd'hui (point le plus bas atteint aujourd'hui : aucune ligne). */
  throughIndex: z.int().min(0).nullable(),
  /** Date du solde expliqué : dimanche de la semaine `throughIndex`, ou aujourd'hui. */
  date: IsoDate,
  startingBalanceCents: Cents.nullable(),
  lines: z.array(CashflowExplanationLine),
  balanceCents: Cents.nullable(),
});
export type CashflowExplanation = z.infer<typeof CashflowExplanation>;

/** Lot Q : facture échue depuis plus de N jours, non comptée dans la trésorerie. */
export const UncountedOverdueInvoice = z.object({
  invoiceId: Uuid,
  number: z.string().nullable(),
  clientName: z.string().nullable(),
  dueDate: IsoDate,
  /** Reste dû HT. */
  amountCents: Cents,
  daysLate: z.int().min(0),
});
export type UncountedOverdueInvoice = z.infer<typeof UncountedOverdueInvoice>;

export const CashflowMetrics = z.object({
  today: IsoDate,
  weeks: z.int(),
  /** Solde de départ : Axonaut (`/bank-accounts`), sinon réglage `cashflow`, sinon inconnu. */
  startingBalance: z.object({
    amountCents: Cents.nullable(),
    source: z.enum(['axonaut', 'setting', 'none']),
    /** Date ou horodatage du solde, si connu (Axonaut : heure de la synchro ; réglage : date de la saisie). */
    at: z.string().nullable(),
    /** Lot Q : Axonaut seulement, relevé bancaire le plus ancien des comptes (`updated_at`), si connu. */
    bankUpdatedAt: z.string().nullable().optional(),
  }),
  /** Semaines du lundi au dimanche, la semaine 0 contenant la date du jour. */
  buckets: z.array(CashflowBucket),
  /**
   * Point le plus bas, null si le solde est inconnu. Lot Q : le solde d'aujourd'hui compte (`isToday`, alors
   * `index = 0` et `start` = aujourd'hui) ; sinon la semaine au solde de fin de semaine le plus bas. En cas
   * d'égalité, le plus tôt (aujourd'hui d'abord). Le point le plus bas est donc toujours ≤ au solde actuel.
   */
  lowest: z.object({ index: z.int(), start: IsoDate, balanceCents: Cents, isToday: z.boolean() }).nullable(),
  /**
   * Lot Q : ce que contiennent le solde en fin d'horizon (`end`) et le point le plus bas (`lowest`, null si le
   * solde est inconnu). `startingHint` : d'où vient le solde actuel (« solde Axonaut du 13 octobre »).
   */
  explanation: z.object({
    startingHint: z.string(),
    end: CashflowExplanation,
    lowest: CashflowExplanation.nullable(),
  }),
  /**
   * Lot Q : factures échues depuis plus de `afterDays` jours (`settings.rules.cashflowOverdue`), non comptées
   * dans les entrées, de la plus ancienne à la plus récente.
   */
  uncountedOverdue: z.object({
    afterDays: z.int().min(0),
    count: z.int().min(0),
    amountCents: Cents,
    invoices: z.array(UncountedOverdueInvoice),
  }),
  /** Solde en fin d'horizon et écart avec le solde de départ (null si inconnu). */
  end: z.object({ balanceCents: Cents.nullable(), deltaCents: Cents.nullable() }),
  /** Devis en attente (hors devis à 0 €, lot Q), pour les scénarios « et si ces devis signaient ? » (calculés par le front). */
  pendingQuotes: z.array(PendingQuote),
  /** Hypothèses des scénarios (maquette : acompte de 50 % sous 3 semaines, solde 6 semaines plus tard). */
  scenario: z.object({ depositRate: z.number(), depositDelayWeeks: z.int(), balanceDelayWeeks: z.int() }),
  /**
   * Lot AG [16] : « Solde au <fin d'exercice> » : même calcul que les semaines, arrêté au jour de fin d'exercice
   * (réglage `fiscalYear.fiscalYearEnd`, 31/12 par défaut ; la prochaine fin d'exercice à partir d'aujourd'hui),
   * prolongé au-delà des 13 semaines quand il le faut. `explanation.date` = ce jour ; `balanceCents` null si le
   * solde de départ est inconnu.
   */
  fiscalYearEnd: z.object({
    date: IsoDate,
    /** Réglage tel qu'enregistré (`MM-JJ`). */
    setting: z.string(),
    /** La date tombe au-delà de l'horizon des semaines affichées. */
    beyondHorizon: z.boolean(),
    explanation: CashflowExplanation,
  }),
});
export type CashflowMetrics = z.infer<typeof CashflowMetrics>;

// ------------------------------------------------------------------ GET /api/metrics/subscriptions

/** Statuts Stripe comptés comme payants (revenu mensuel récurrent et nombre d'abonnés). */
export const PAYING_SUBSCRIPTION_STATUSES = ['active', 'past_due'] as const;

/** D'où vient un abonnement : Stripe (lu via le back) ou saisi dans le cockpit (hors Stripe, lot J). */
export const SubscriptionSource = z.enum(['stripe', 'manual']);
export type SubscriptionSource = z.infer<typeof SubscriptionSource>;

export const SubscriptionsMetrics = z.object({
  /**
   * Somme des montants mensuels des abonnements payants : `monthly_amount_cents` des abonnements Stripe
   * (déjà ramenés au mois par le back) + abonnements hors Stripe actifs (annuel / 12).
   */
  mrrCents: Cents,
  arrCents: Cents,
  /** Abonnés payants, hors Stripe compris. */
  payingCount: z.int().min(0),
  /** Part hors Stripe du revenu mensuel récurrent, et nombre d'abonnements hors Stripe actifs (lot J). */
  manualMrrCents: Cents,
  manualCount: z.int().min(0),
  /**
   * Par formule, de la moins chère à la plus chère (montant mensuel moyen). Un abonnement hors Stripe
   * compte sous son libellé, avec `source: 'manual'` (jamais fusionné avec une formule Stripe du même nom).
   */
  byPlan: z.array(
    z.object({ plan: z.string(), count: z.int().min(0), mrrCents: Cents, source: SubscriptionSource }),
  ),
  /** Nombre d'abonnements Stripe par statut brut (active, past_due, trialing, canceled…) ; hors Stripe exclus. */
  statusCounts: z.record(z.string(), z.int().min(0)),
  /** Abonnements impayés (`past_due_since` renseigné ou statut past_due/unpaid). */
  pastDue: z.array(
    z.object({
      subscriptionId: Uuid,
      clientId: Uuid.nullable(),
      clientName: z.string().nullable(),
      siteName: z.string().nullable(),
      plan: z.string().nullable(),
      monthlyAmountCents: Cents,
      status: z.string(),
      pastDueSince: IsoDateTime.nullable(),
    }),
  ),
  /** Sites en ligne sans abonnement payant (un abonnement hors Stripe actif du client compte). */
  liveSitesWithoutSubscription: z.int().min(0),
  /** Tous les abonnements hors Stripe (actifs, à venir, terminés), lisibles par les membres (lot J). */
  manual: z.array(ManualSubscription),
});
export type SubscriptionsMetrics = z.infer<typeof SubscriptionsMetrics>;

// ------------------------------------------------------------------ GET /api/metrics/acquisition

export const AcquisitionChannelCount = z.object({
  key: z.string(),
  label: z.string(),
  count: z.int().min(0),
});
export type AcquisitionChannelCount = z.infer<typeof AcquisitionChannelCount>;

export const AcquisitionMetrics = z.object({
  /**
   * Nouveaux clients par canal et par année. Un mois couvert par l'historique importé (lignes
   * `channel:*` du Suivi CA) est compté depuis l'historique ; les autres mois, depuis les devis acceptés
   * attribués dans l'appli (`quotes.acquisition_channel`, ou un apporteur = « Partenaires webdesign »).
   */
  years: z.array(
    z.object({
      year: z.int(),
      total: z.int().min(0),
      /** Du plus grand au plus petit. */
      channels: z.array(AcquisitionChannelCount),
      fromHistory: z.int().min(0),
      fromQuotes: z.int().min(0),
      /** Devis acceptés des mois hors historique sans canal ni apporteur. */
      unattributedQuotes: z.int().min(0),
    }),
  ),
});
export type AcquisitionMetrics = z.infer<typeof AcquisitionMetrics>;

// ------------------------------------------------------------------ PUT /api/admin/objectives/:year

/** `:year` des objectifs. */
export const ObjectivesYearParams = z.object({ year: z.coerce.number().int().min(2000).max(2100) });
export type ObjectivesYearParams = z.infer<typeof ObjectivesYearParams>;

/**
 * `PUT /api/admin/objectives/:year` (administrateur) : objectifs mensuels de CA (table `objectives`, source
 * de vérité), en centimes HT. Upsert des mois envoyés (1 à 12, tous dans l'année, sans doublon) ; les mois
 * absents ne changent pas.
 */
export const UpdateObjectivesBody = z.object({
  months: z
    .array(z.object({ month: MonthKey, amountCents: Cents.min(0) }))
    .min(1)
    .max(12),
});
export type UpdateObjectivesBody = z.infer<typeof UpdateObjectivesBody>;

/** Objectifs d'une année : 12 mois de janvier à décembre (`null` = pas d'objectif). */
export const ObjectivesYear = z.object({
  year: z.int(),
  months: z.array(z.object({ month: MonthKey, amountCents: Cents.nullable() })).length(12),
  totalCents: Cents,
});
export type ObjectivesYear = z.infer<typeof ObjectivesYear>;

// ------------------------------------------------------------------ GET /api/metrics/revenue-details?year= (lot V)

/**
 * Lot V « Chiffres » (retours d'Inès du 13/10/2026, changements [1]-[10]) : tout ce que l'onglet Chiffres affiche
 * en plus des routes `overview`, `revenue`, `subscriptions`, selon l'interrupteur de page Signé / Facturé [1].
 *
 * Définitions (CDC, « Retours d'Inès ») :
 * - Signé = devis acceptés (date de réponse) + abonnements démarrés dans la période × 12 × mensuel ;
 * - Facturé = factures Axonaut datées de la période, abonnements compris (`invoices.kind = 'subscription'`) ;
 * - devis à 0 € (`isZeroAmount`) exclus partout, taux de conversion compris ;
 * - début d'un abonnement Stripe : première facture d'abonnement Axonaut du client, sinon première vue par la
 *   synchro (sauf les abonnements déjà là à la toute première synchro : début inconnu, jamais comptés en Signé) ;
 *   hors Stripe : `start_date`. Fin : Stripe `cancelAt`, sinon `currentPeriodEnd` d'un abonnement résilié ; hors
 *   Stripe : `end_date` ;
 * - préséance par mois (Axonaut, sinon historique importé du Sheet) comme `RevenueMetrics`.
 */
export const RevenueMode = z.enum(['signed', 'invoiced']);
export type RevenueMode = z.infer<typeof RevenueMode>;

export const CategoryAmount = z.object({ category: ProductCategory, amountCents: Cents });
export type CategoryAmount = z.infer<typeof CategoryAmount>;

/** Une série du mois (facturé ou signé). */
export const DetailSeries = z.object({
  /** null pour un mois futur. */
  amountCents: Cents.nullable(),
  /** Part des abonnements (facturé : factures d'abonnement ; signé : démarrés × 12 × mensuel), null si inconnue. */
  subscriptionsCents: Cents.nullable(),
  /**
   * Par catégorie de produit (abonnements compris), même total que `amountCents` ; null si inconnu (mois futur,
   * mois venant de l'historique sans détail par produit).
   */
  byCategory: z.array(CategoryAmount).nullable(),
  /** Même série, même mois de l'année précédente (null si inconnu). */
  previousYearCents: Cents.nullable(),
});
export type DetailSeries = z.infer<typeof DetailSeries>;

/**
 * Facturé prévu d'un mois (mois en cours : ce qui reste à facturer jusqu'à la fin du mois ; mois suivants de
 * l'année en cours) : même modèle que la trésorerie, en HT. `balancesCents` : reste à facturer des devis signés
 * (au R5 du projet + 15 jours ; lot AE : projet pas encore calé placé avec le simulateur du planning ; en retard →
 * mois en cours) ; `depositsCents` :
 * acomptes pas encore facturés (aujourd'hui + 15 jours) ; `subscriptionsCents` : abonnements Stripe chaque mois
 * (mois en cours : moins les factures d'abonnement déjà émises), hors Stripe à leurs dates de facturation.
 */
export const InvoicingForecast = z.object({
  balancesCents: Cents,
  depositsCents: Cents,
  subscriptionsCents: Cents,
  totalCents: Cents,
});
export type InvoicingForecast = z.infer<typeof InvoicingForecast>;

/** Nombres du mois (ou de la période) sur les clients [8]. */
export const ClientCounts = z.object({
  /** Nouveaux clients non partenaires (premier devis signé ; Facturé : à la date de sa première facture). */
  newEvolusite: z.int().min(0),
  /** Nouveaux clients partenaires (`isPartnerClient`). */
  newPartner: z.int().min(0),
  /** Ventes additionnelles : devis signés d'un client qui avait déjà signé. */
  additional: z.int().min(0),
  /** Clients perdus : fin d'abonnement à sa date de fin (un client qui garde un abonnement payant n'est pas perdu). */
  lost: z.int().min(0),
});
export type ClientCounts = z.infer<typeof ClientCounts>;

export const DetailMonth = z.object({
  month: MonthKey,
  /** Mois postérieur au mois en cours. */
  future: z.boolean(),
  /** Mois en cours. */
  current: z.boolean(),
  source: MonthSource,
  objectiveCents: Cents.nullable(),
  invoiced: DetailSeries,
  signed: DetailSeries,
  /**
   * Lot AG [12] : les deux parts du CA signé (lignes « Devis signés » et « Abonnements signés (× 12) » du tableau) :
   * devis acceptés hors devis à 0 € (mois du Sheet : le signé de l'historique) et abonnements démarrés × 12 (null
   * pour un mois du Sheet). null pour un mois futur. `signedQuotesCents + (signedSubscriptionsCents ?? 0)` =
   * `signed.amountCents`.
   */
  signedQuotesCents: Cents.nullable(),
  signedSubscriptionsCents: Cents.nullable(),
  cashed: z.object({ amountCents: Cents.nullable(), previousYearCents: Cents.nullable() }),
  /** Mois en cours et suivants de l'année en cours seulement, sinon null. Signé et encaissé : pas de prévision. */
  forecast: InvoicingForecast.nullable(),
  /** Devis du mois, hors devis à 0 € ; null pour un mois futur. */
  quotes: z
    .object({
      issuedCount: z.int().min(0),
      issuedCents: Cents,
      signedCount: z.int().min(0),
      signedCents: Cents,
      lostCount: z.int().min(0),
      lostCents: Cents,
      /**
       * Lot BM [5] : devis en attente à la fin du mois (au plus tard aujourd'hui pour le mois en cours) : émis au plus
       * tard ce jour-là, sans réponse à cette date (réponse après, ou aucune), hors devis à 0 €, montant HT (comme la
       * tuile « Devis en attente »). null pour un mois venu du Google Sheet (pas de détail).
       */
      pendingEnd: z
        .object({ count: z.int().min(0), amountCents: Cents })
        .nullable()
        .default(null),
      /** Lot BM [6] : panier moyen des devis signés du mois (`signedCents ÷ signedCount`), null sans devis signé. */
      signedBasketCents: Cents.nullable().default(null),
      /** Lot BM [6] : panier moyen des devis perdus du mois (`lostCents ÷ lostCount`), null sans devis perdu. */
      lostBasketCents: Cents.nullable().default(null),
    })
    .nullable(),
  /** null pour un mois futur. */
  clients: z.object({ signed: ClientCounts, invoiced: ClientCounts }).nullable(),
  /** Panier moyen création du mois (cf. `Basket`), null sans devis de création. */
  creationBasket: z.object({ signedCents: Cents.nullable(), invoicedCents: Cents.nullable() }),
});
export type DetailMonth = z.infer<typeof DetailMonth>;

/** Ligne de la carte « Abonnements par formule » [6]. */
export const SubscriptionPlanRow = z.object({
  /** Clé stable (formule normalisée, `manual-other` ou `other-invoiced`). */
  key: z.string(),
  /** Formule (Stripe : formule de base lue dans le back ; hors Stripe : formule choisie, sinon « Autre (hors Stripe) »). */
  label: z.string(),
  /** Abonnés payants aujourd'hui (Stripe active/past_due, hors Stripe en cours). */
  subscribers: z.int().min(0),
  mrrCents: Cents,
  /** Abonnements démarrés dans la période × 12 × mensuel. */
  signedCents: Cents,
  /** Lignes de factures d'abonnement de la période rattachées à la formule par libellé normalisé. */
  invoicedCents: Cents,
});
export type SubscriptionPlanRow = z.infer<typeof SubscriptionPlanRow>;

export const BasketKind = z.enum(['creation', 'integration', 'refonte']);
export type BasketKind = z.infer<typeof BasketKind>;

/**
 * Panier moyen [7] : création = devis surtout vitrine ou e-commerce d'un client non partenaire ; intégration =
 * client partenaire ; refonte à part (devis surtout refonte). Signé : montant HT des devis acceptés dans la
 * période ; Facturé : total des factures liées à chaque devis signé dont la première facture tombe dans la
 * période.
 */
export const Basket = z.object({
  kind: BasketKind,
  label: z.string(),
  count: z.int().min(0),
  totalCents: Cents,
  averageCents: Cents.nullable(),
});
export type Basket = z.infer<typeof Basket>;

/** Lot BM [4] : ligne « Tous » du panier moyen (tous les devis, quel que soit leur type). */
export const BasketTotal = z.object({
  count: z.int().min(0),
  totalCents: Cents,
  averageCents: Cents.nullable(),
});
export type BasketTotal = z.infer<typeof BasketTotal>;
const EMPTY_BASKET_TOTAL: BasketTotal = { count: 0, totalCents: 0, averageCents: null };

export const ClientsSummary = z.object({
  newEvolusite: z.object({ count: z.int().min(0), amountCents: Cents }),
  newPartner: z.object({ count: z.int().min(0), amountCents: Cents }),
  additional: z.object({ count: z.int().min(0), amountCents: Cents }),
  /** Montant mensuel des abonnements perdus. */
  lost: z.object({ count: z.int().min(0), mrrCents: Cents }),
});
export type ClientsSummary = z.infer<typeof ClientsSummary>;

/**
 * Nouveaux clients de la période et CA de ces clients dans la période. Lot AM [38] : `conversionRate` = nouveaux
 * clients ÷ R1 réalisés de la ligne (null sans R1 ; peut dépasser 1 : un client partenaire n'a pas toujours de R1).
 */
export const AcquisitionFigures = z.object({
  clients: z.int().min(0),
  amountCents: Cents,
  conversionRate: z.number().min(0).nullable(),
});
export type AcquisitionFigures = z.infer<typeof AcquisitionFigures>;

export const AcquisitionRow = z.object({
  /** Canal (`none` = non renseigné) ou identifiant de l'apporteur (`none` = sans apporteur). */
  key: z.string(),
  label: z.string(),
  /** Par apporteur : canal le plus fréquent de ses clients. */
  channelLabel: z.string().nullable(),
  /**
   * Lot AM [38] : clients distincts avec un R1 passé dans la période (agendas, copies d'un même événement comptées
   * une fois) ; un prospect sans fiche Axonaut compte dans « Non renseigné » / « Sans apporteur ».
   */
  r1Clients: z.int().min(0),
  /** Lot AM [38] : clients distincts avec un devis daté de la période (hors devis à 0 €). */
  quotedClients: z.int().min(0),
  signed: AcquisitionFigures,
  invoiced: AcquisitionFigures,
});
export type AcquisitionRow = z.infer<typeof AcquisitionRow>;

export const QuoteFigures = z.object({ count: z.int().min(0), amountCents: Cents });
export type QuoteFigures = z.infer<typeof QuoteFigures>;

export const RevenueDetailsMetrics = z.object({
  year: z.int(),
  today: IsoDate,
  /** Mois en cours s'il est dans l'année, sinon null. */
  currentMonth: MonthKey.nullable(),
  /** Mois couverts par les cartes [5]-[10] : janvier au mois en cours (année en cours), sinon l'année entière. */
  period: z.object({ from: MonthKey, through: MonthKey }),
  months: z.array(DetailMonth).length(12),
  /**
   * Lot AG [8] : la grande tuile (« CA signé 2025 », « CA facturé 2026 ») : somme des mois de la période (janvier au
   * mois en cours, l'année entière pour une année passée), mêmes valeurs que les lignes du tableau.
   */
  totals: z.object({ signedCents: Cents, invoicedCents: Cents }),
  /**
   * « Atterrissage fin d'année » [2] (année en cours seulement) : facturé de l'année + facturé prévu au 31/12. Lot AG
   * [12] : plus comparé aux objectifs (ce sont des objectifs de CA SIGNÉ).
   */
  yearEnd: z.object({ invoicedCents: Cents, forecastCents: Cents, totalCents: Cents }).nullable(),
  /**
   * « CA par produit » [5] sur la période, abonnements compris, du plus gros au plus petit. Lot AY : le Signé compte
   * la part d'Evolusite (une ligne sous-traitée sort de son produit à hauteur de son coût, un montant global au
   * prorata) ; `subcontractedCents` = sous-traité de la période, affiché à part (« Sous-traité (hors total) ») ; le
   * Facturé reste brut.
   */
  products: z.object({
    signed: z.array(ProductMixItem),
    invoiced: z.array(ProductMixItem),
    subcontractedCents: Cents.default(0),
  }),
  /** « Abonnements par formule » [6] ; total : un abonné compte une fois. */
  subscriptions: z.object({
    plans: z.array(SubscriptionPlanRow),
    total: z.object({
      subscribers: z.int().min(0),
      mrrCents: Cents,
      signedCents: Cents,
      invoicedCents: Cents,
    }),
  }),
  baskets: z.object({
    signed: z.array(Basket),
    invoiced: z.array(Basket),
    /**
     * Lot BM [4] : devis perdus (refusés, date de réponse dans la période), classés comme les signés ; montant du
     * devis en part d'Evolusite, quel que soit l'interrupteur (un devis perdu n'est jamais facturé).
     */
    lost: z.array(Basket).default([]),
    /** Lot BM [4] : ligne « Tous » : tous les devis signés (selon le mode) ou perdus de la période, hors 0 €. */
    all: z
      .object({ signed: BasketTotal, invoiced: BasketTotal, lost: BasketTotal })
      .default({ signed: EMPTY_BASKET_TOTAL, invoiced: EMPTY_BASKET_TOTAL, lost: EMPTY_BASKET_TOTAL }),
  }),
  clients: z.object({ signed: ClientsSummary, invoiced: ClientsSummary }),
  /**
   * « D'où viennent les clients » [9] : canal et apporteur de la fiche Axonaut, sinon du Cockpit. Lot AM [38] : une
   * ligne dès qu'elle a un R1 réalisé, un devis émis ou un nouveau client.
   */
  acquisition: z.object({
    byChannel: z.array(AcquisitionRow),
    byPartner: z.array(AcquisitionRow),
    /**
     * Lot AM [38] : depuis quand l'agenda est lu (`from` : jour depuis lequel TOUS les agendas lus sont couverts,
     * null = aucun agenda lu). `complete` = la période commence ce jour-là ou après ; sinon la colonne « R1
     * réalisés » ne compte que depuis `from` et la conversion est vide (null) sur chaque ligne.
     */
    r1Coverage: z.object({ from: IsoDate.nullable(), complete: z.boolean() }),
  }),
  /** « Devis » [10] sur la période, hors devis à 0 € ; `pending` = tous les devis en attente aujourd'hui. */
  quotes: z.object({
    issued: QuoteFigures,
    signed: QuoteFigures,
    lost: QuoteFigures,
    pending: QuoteFigures,
    /** Signés / émis en nombre, null sans devis émis. */
    conversionRate: z.number().nullable(),
    /** Signés / émis en montant, null sans montant émis. */
    amountConversionRate: z.number().nullable(),
    /**
     * R1 de la période : historique importé pour les mois qu'il couvre (jusqu'à son dernier mois de R1), puis les
     * rendez-vous R1 des agendas (copies d'un même événement comptées une fois) ; null sans historique ni agenda.
     */
    r1: z.int().min(0).nullable(),
  }),
});
export type RevenueDetailsMetrics = z.infer<typeof RevenueDetailsMetrics>;

// ------------------------------------------------------------------ GET /api/metrics/detail (lot AG [13])

/**
 * Lot AG [13] « Fenêtre de détail » : les lignes qui composent un chiffre de Chiffres ou de Trésorerie (devis,
 * factures, abonnements, clients…), calculées par LE MÊME code que le chiffre (`apps/server/src/metrics/details.ts`,
 * `compute.ts`). `kind` dit quel chiffre :
 * - `signed`, `signed-quotes`, `signed-subscriptions` : CA signé, ses devis, ses abonnements démarrés (× 12) ;
 * - `invoiced`, `invoiced-subscriptions` : factures (dont abonnements) ;
 * - `forecast`, `forecast-balances`, `forecast-deposits`, `forecast-subscriptions` : facturé prévu (avec l'origine
 *   de chaque montant), `invoiced-total` : facturé + prévu ;
 * - `quotes-issued`, `quotes-signed`, `quotes-lost`, `quotes-pending` : devis ;
 * - `clients-new`, `clients-partner`, `clients-additional`, `clients-lost` : clients (selon `mode`) ;
 * - `basket` (`key` = creation | integration | refonte), `acquisition-channel` / `acquisition-partner` (`key` = la
 *   ligne), `product` (`key` = catégorie), `plan` (abonnés d'une formule, `key` = formule), `plan-amount` (signé ou
 *   facturé d'une formule), `unpaid` (`key` = abonnement impayé) ;
 * - `cashflow` (`key` = invoices | subscriptions | signed_quotes | expenses, `horizon` = weeks | fiscal).
 * `month` absent = la période des cartes (janvier au mois en cours, l'année entière pour une année passée).
 */
export const FigureDetailKind = z.enum([
  'signed',
  'signed-quotes',
  'signed-subscriptions',
  'invoiced',
  'invoiced-subscriptions',
  'forecast',
  'forecast-balances',
  'forecast-deposits',
  'forecast-subscriptions',
  'invoiced-total',
  'quotes-issued',
  'quotes-signed',
  'quotes-lost',
  'quotes-pending',
  'clients-new',
  'clients-partner',
  'clients-additional',
  'clients-lost',
  'basket',
  // Lot BM [4] [5] [6] : paniers perdus (`key` = creation | integration | refonte | all), devis en attente à la
  // fin d'un mois, paniers moyens des devis signés et perdus d'un mois.
  'basket-lost',
  'quotes-pending-end',
  'quotes-signed-basket',
  'quotes-lost-basket',
  'acquisition-channel',
  'acquisition-partner',
  'product',
  'plan',
  'plan-amount',
  'unpaid',
  'cashflow',
]);
export type FigureDetailKind = z.infer<typeof FigureDetailKind>;

export const FigureDetailQuery = z.object({
  kind: FigureDetailKind,
  year: z.coerce.number().int().min(2000).max(2100).optional(),
  month: MonthKey.optional(),
  mode: RevenueMode.optional(),
  key: z.string().min(1).max(200).optional(),
  horizon: z.enum(['weeks', 'fiscal']).optional(),
});
export type FigureDetailQuery = z.infer<typeof FigureDetailQuery>;

export const FigureDetailLink = z.object({ label: z.string(), url: z.string() });
export type FigureDetailLink = z.infer<typeof FigureDetailLink>;

export const FigureDetailRow = z.object({
  /** Client (ou libellé de la ligne quand ce n'est pas un client : poste de dépense, mois du Sheet…). */
  client: z.string().nullable(),
  /** Précisions : n°, produit, date, canal, origine d'un montant prévu… */
  details: z.string(),
  /** Montant HT de la ligne (null quand la ligne n'a pas de montant). */
  amountCents: Cents.nullable(),
  /** Nombre que la ligne apporte au chiffre (1 par client ; un mois du Sheet apporte son nombre). */
  count: z.int().min(0),
  /** Texte de la colonne de droite quand ce n'est pas un montant (« 79 €/mois »). */
  note: z.string().nullable(),
  links: z.array(FigureDetailLink),
  /** Lot AM [38] : valeurs des colonnes en plus (`FigureDetail.columns`, même ordre), null = vide. */
  cells: z.array(z.string().nullable()).optional(),
});
export type FigureDetailRow = z.infer<typeof FigureDetailRow>;

export const FigureDetail = z.object({
  title: z.string(),
  subtitle: z.string(),
  /** En-tête de la colonne des montants (« Montant HT », « Mensuel HT »). */
  amountLabel: z.string(),
  /**
   * Lot AM [38] : colonnes en plus, entre le client et le montant (« R1 », « Devis », « Signé »), avec leur total :
   * le nombre de lignes où la colonne est remplie, égal au chiffre de la carte (testé).
   */
  columns: z.array(z.object({ label: z.string(), total: z.int().min(0) })).optional(),
  rows: z.array(FigureDetailRow),
  /** Somme des lignes : nombre et montant (null quand les lignes n'ont pas de montant). */
  totals: z.object({ count: z.int().min(0), amountCents: Cents.nullable() }),
  /**
   * Le chiffre cliqué, calculé comme à l'écran : `cents` = `totals.amountCents`, `count` = `totals.count` (au
   * centime près, testé) ; `none` = pas de total (fenêtre d'un impayé).
   */
  figure: z.object({ kind: z.enum(['cents', 'count', 'none']), value: z.number().nullable() }),
  /** Faits d'une fenêtre sans lignes (impayé : formule, statut, depuis quand…). */
  facts: z.array(z.object({ label: z.string(), value: z.string() })),
  /** Liens de la fenêtre (impayé : client dans Stripe, Axonaut). */
  links: z.array(FigureDetailLink),
  notes: z.array(z.string()),
});
export type FigureDetail = z.infer<typeof FigureDetail>;

// ------------------------------------------------------------------ Simulation des devis en attente (lot BL)

/**
 * Lot BL : `POST /api/metrics/simulate` : les devis en attente cochés, dans l'ordre de la liste (le planning les
 * place dans cet ordre). Rien n'est enregistré.
 */
export const MetricsSimulateBody = z.object({ quoteIds: z.array(Uuid).max(100) });
export type MetricsSimulateBody = z.infer<typeof MetricsSimulateBody>;

/**
 * Réponse : signé de l'année (réel, devis seuls, et simulé, en part d'Evolusite comme les Chiffres) contre
 * l'objectif annuel ; facturation attendue par mois, du mois en cours au dernier mois concerné (`baseCents` : reste à
 * facturer des devis déjà signés, `simCents` : celui des devis cochés, mêmes règles que `amountsToInvoice`) ; mise en
 * ligne simulée de chaque devis coché (`r5` null : pas de projet de site, solde à J+60).
 */
export const MetricsSimulateResult = z.object({
  year: z.int(),
  today: IsoDate,
  realSignedCents: Cents,
  simSignedCents: Cents,
  goalCents: Cents,
  invoicing: z.array(z.object({ month: MonthKey, baseCents: Cents, simCents: Cents })),
  placements: z.array(
    z.object({
      quoteId: Uuid,
      r5: IsoDate.nullable(),
      /** Ajouts du lot BL (Trésorerie) : dates attendues de l'acompte et du solde (null : rien à facturer). */
      depositOn: IsoDate.nullable().default(null),
      balanceOn: IsoDate.nullable().default(null),
    }),
  ),
  /**
   * Ajout du lot BL : devis déjà signés sans projet calé, placés avant les devis cochés (comme dans la Trésorerie) ;
   * la simulation de plusieurs devis de Projets ne compte que les projets existants.
   */
  signedUnplanned: z.int().default(0),
});
export type MetricsSimulateResult = z.infer<typeof MetricsSimulateResult>;

/**
 * Lot BL : `GET /api/metrics/win-rate` : taux de signature des 12 derniers mois (mois en cours compris) : devis
 * signés ÷ devis émis, hors devis à 0 €, comme la conversion des Chiffres (mois du Sheet : son historique). null :
 * aucun devis émis sur la période.
 */
export const WinRateMetrics = z.object({
  from: MonthKey,
  to: MonthKey,
  issued: z.int(),
  signed: z.int(),
  rate: z.number().nullable(),
});
export type WinRateMetrics = z.infer<typeof WinRateMetrics>;
